/**
 * Loading / empty / error states. Every data region renders exactly one of these or its content,
 * so a page never shows a blank box while a request is pending or after it failed.
 */
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { ApiError, errorMessage } from "../api/client";
import { Button, Spinner } from "./ui";

export function LoadingState({ label = "Loading…", className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-center gap-2 py-6 text-sm text-zinc-500 dark:text-zinc-400 ${className ?? ""}`}
    >
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-md border border-dashed border-zinc-300 px-4 py-8 text-center dark:border-zinc-700">
      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{title}</p>
      {children ? (
        <div className="mx-auto mt-1 max-w-prose text-sm text-zinc-500 dark:text-zinc-400">{children}</div>
      ) : null}
      {action ? <div className="mt-3 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * Error state. A 404 usually means the data is not there (a state that was never loaded, a
 * security not screened yet, another tenant's id) rather than a fault, so it reads calmly instead
 * of implying the user did something wrong; the server's problem detail says which.
 */
export function ErrorState({
  error,
  onRetry,
  what = "data",
}: {
  error: unknown;
  onRetry?: () => void;
  what?: string;
}) {
  const status = error instanceof ApiError ? error.status : null;
  const notAvailable = status === 404 || status === 405 || status === 501;
  // "Not loaded / not available" is a state of the data, not a failure: keep it calm.
  return (
    <div
      role={notAvailable ? "status" : "alert"}
      className={
        notAvailable
          ? "rounded-md border border-zinc-300 bg-zinc-50 px-4 py-3 text-sm text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
          : "rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
      }
    >
      <p className="font-medium">
        {notAvailable ? `No ${what} available` : `Could not load ${what}`}
        {status ? <span className="ml-1 font-normal opacity-70">(HTTP {status})</span> : null}
      </p>
      <p className="mt-0.5 text-[13px] opacity-90">{errorMessage(error)}</p>
      {onRetry ? (
        <Button size="sm" variant="secondary" className="mt-2" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Render a query's three states. `isEmpty` decides when successful data counts as empty.
 * Background refetches keep showing the previous data (no flash), per the dataviz guidance.
 */
export function QueryState<T>({
  query,
  what,
  empty,
  isEmpty,
  loadingLabel,
  children,
}: {
  query: UseQueryResult<T>;
  what?: string;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  loadingLabel?: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingState label={loadingLabel} />;
  if (query.isError && query.data === undefined)
    return <ErrorState error={query.error} what={what} onRetry={() => void query.refetch()} />;
  const data = query.data as T;
  if (empty !== undefined && isEmpty?.(data)) return <>{empty}</>;
  return <>{children(data)}</>;
}
